import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
    Drawer,
    Stack,
    Text,
    Group,
    ScrollArea,
    Paper,
    Textarea,
    Button,
    Loader,
    Badge,
    Divider,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconBrandWhatsapp, IconRefresh, IconSend } from "@tabler/icons-react";
import {
    getTransportRequestMessages,
    markTransportRequestMessagesRead,
    sendTransportRequestMessage,
    attachMessageToTransportRequest,
} from "../../../api/transportAdminApi";

type WhatsAppMessageDto = {
    id: number;
    messageId: string;
    phoneNumber: string;
    direction: string;
    type: string;
    textBody: string;
    status?: string | null;
    errorMessage?: string | null;
    createdAtUtc: string;
    sentAtUtc?: string | null;
    deliveredAtUtc?: string | null;
    readAtUtc?: string | null;
    isReadByAdmin: boolean;
    transportRequestId?: number | null;
};

type Props = {
    opened: boolean;
    onClose: () => void;
    bookingId: number | null;
    contactName?: string | null;
    phoneNumber?: string | null;
};

function formatDateTime(value?: string | null) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
}

function getStatusColor(status?: string | null) {
    const s = String(status ?? "").toLowerCase();

    if (["delivered", "read"].includes(s)) return "green";
    if (["sent", "queued", "accepted"].includes(s)) return "blue";
    if (["failed", "undelivered"].includes(s)) return "red";
    if (["received"].includes(s)) return "gray";

    return "gray";
}

export default function TransportMessageDrawer({
    opened,
    onClose,
    bookingId,
    contactName,
    phoneNumber,
}: Props) {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [messages, setMessages] = useState<WhatsAppMessageDto[]>([]);
    const [message, setMessage] = useState("");
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const wasNearBottomRef = useRef(true);
    const previousMessageCountRef = useRef(0);

    const loadMessages = async (silent = false) => {
        if (!bookingId) return;

        const el = viewportRef.current;

        if (el) {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

            restoreScrollRef.current = {
                shouldStickToBottom: distanceFromBottom < 120,
                prevScrollTop: el.scrollTop,
                prevScrollHeight: el.scrollHeight,
            };
        } else {
            restoreScrollRef.current = {
                shouldStickToBottom: true,
                prevScrollTop: 0,
                prevScrollHeight: 0,
            };
        }

        if (!silent) setLoading(true);

        try {
            const data = await getTransportRequestMessages(bookingId);
            setMessages(data);
        } catch (e: any) {
            if (!silent) {
                showNotification({
                    color: "red",
                    title: "Fejl",
                    message: "Kunne ikke hente WhatsApp-beskeder.",
                });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };
    const markAsRead = async () => {
        if (!bookingId) return;

        try {
            await markTransportRequestMessagesRead(bookingId);
        } catch {
            // ingen notification nødvendig her
        }
    };

    const isNearBottom = () => {
        const el = viewportRef.current;
        if (!el) return true;

        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        return distanceFromBottom < 120;
    };

    const scrollToBottom = (smooth = false) => {
        bottomRef.current?.scrollIntoView({
            behavior: smooth ? "smooth" : "auto",
            block: "end",
        });
    };
    const restoreScrollRef = useRef<{
        shouldStickToBottom: boolean;
        prevScrollTop: number;
        prevScrollHeight: number;
    } | null>(null);


    const handleAttach = async (messageId: number) => {
        if (!bookingId) return;

        try {
            await attachMessageToTransportRequest(messageId, bookingId);

            showNotification({
                color: "green",
                title: "Koblet",
                message: "Beskeden er nu koblet til denne booking.",
            });

            await loadMessages();
        } catch (e: any) {
            const msg =
                e?.response?.data && typeof e.response.data === "string"
                    ? e.response.data
                    : "Kunne ikke koble beskeden til bookingen.";

            showNotification({
                color: "red",
                title: "Fejl",
                message: msg,
            });
        }
    };
    const refreshRef = useRef<number | null>(null);
    useEffect(() => {
        if (!opened || !bookingId) return;

        const init = async () => {
            await loadMessages();
            await markAsRead();
            await loadMessages();
            setTimeout(() => scrollToBottom(false), 0);
        };

        init();

        refreshRef.current = window.setInterval(() => {
            loadMessages();
        }, 5000);

        return () => {
            if (refreshRef.current) {
                window.clearInterval(refreshRef.current);
                refreshRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, bookingId]);

    useLayoutEffect(() => {
        const el = viewportRef.current;
        const restore = restoreScrollRef.current;

        if (!el || !restore) {
            previousMessageCountRef.current = messages.length;
            return;
        }

        if (restore.shouldStickToBottom) {
            el.scrollTop = el.scrollHeight;
        } else {
            const heightDiff = el.scrollHeight - restore.prevScrollHeight;
            el.scrollTop = restore.prevScrollTop + heightDiff;
        }

        previousMessageCountRef.current = messages.length;
        restoreScrollRef.current = null;
    }, [messages]);

    const handleSend = async () => {
        if (!bookingId || !message.trim()) return;

        setSending(true);
        try {
            await sendTransportRequestMessage(bookingId, message.trim());
            setMessage("");

            showNotification({
                color: "green",
                title: "Sendt",
                message: "Beskeden er sendt på WhatsApp.",
            });

            await loadMessages();
        } catch (e: any) {
            const msg =
                e?.response?.data && typeof e.response.data === "string"
                    ? e.response.data
                    : "Kunne ikke sende besked.";

            showNotification({
                color: "red",
                title: "Fejl ved sending",
                message: msg,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="lg"
            title={
                <Group gap="xs">
                    <IconBrandWhatsapp size={18} />
                    <Text fw={700}>
                        WhatsApp chat
                        {contactName ? ` · ${contactName}` : ""}
                    </Text>
                </Group>
            }
        >
            <Stack gap="md" h="100%">
                <Paper withBorder radius="md" p="sm">
                    <Stack gap={4}>
                        {contactName ? (
                            <Text size="sm" fw={600}>
                                Kontakt: {contactName}
                            </Text>
                        ) : null}

                        {phoneNumber ? (
                            <Text size="xs" c="dimmed">
                                Nummer: {phoneNumber}
                            </Text>
                        ) : null}

                        {bookingId ? (
                            <Text size="xs" c="dimmed">
                                Booking ID: {bookingId}
                            </Text>
                        ) : null}
                    </Stack>
                </Paper>

                <Group justify="space-between">
                    <Text fw={600}>Samtale</Text>

                    <Button
                        variant="light"
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => loadMessages(false)}
                        loading={loading}
                    >
                        Opdater
                    </Button>
                </Group>

                <Divider />

                <Paper
                    withBorder
                    radius="md"
                    p="sm"
                    style={{
                        flex: 1,
                        minHeight: 300,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {loading ? (
                        <Group justify="center" py="xl">
                            <Loader />
                        </Group>
                    ) : (
                        <ScrollArea h={420} viewportRef={viewportRef}>
                            <Stack gap="sm">
                                {messages.length === 0 ? (
                                    <Text c="dimmed" size="sm">
                                        Ingen beskeder endnu.
                                    </Text>
                                ) : (
                                    messages.map((m) => {
                                        const isOutbound = m.direction === "outbound";

                                        return (
                                            <Group
                                                key={m.id}
                                                justify={isOutbound ? "flex-end" : "flex-start"}
                                            >
                                                <Paper
                                                    radius="md"
                                                    p="sm"
                                                    withBorder
                                                    style={{
                                                        maxWidth: "82%",
                                                        background: isOutbound ? "#fff1f1" : "#f8f9fa",
                                                    }}
                                                >
                                                    <Stack gap={6}>
                                                        <Group gap="xs">
                                                            <Badge
                                                                size="sm"
                                                                variant="light"
                                                                color={isOutbound ? "red" : "gray"}
                                                            >
                                                                {isOutbound ? "Sendt" : "Modtaget"}
                                                            </Badge>

                                                            {m.status ? (
                                                                <Badge
                                                                    size="sm"
                                                                    variant="light"
                                                                    color={getStatusColor(m.status)}
                                                                >
                                                                    {m.status}
                                                                </Badge>
                                                            ) : null}
                                                        </Group>

                                                        <Text
                                                            size="sm"
                                                            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                        >
                                                            {m.textBody}
                                                        </Text>
                                                        {!m.transportRequestId && bookingId && (
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                onClick={() => handleAttach(m.id)}
                                                            >
                                                                Knyt til denne booking
                                                            </Button>
                                                        )}

                                                        <Text size="xs" c="dimmed">
                                                            {formatDateTime(m.createdAtUtc)}
                                                        </Text>

                                                        {m.errorMessage ? (
                                                            <Text size="xs" c="red">
                                                                Fejl: {m.errorMessage}
                                                            </Text>
                                                        ) : null}
                                                        <div ref={bottomRef} />
                                                    </Stack>
                                                </Paper>
                                            </Group>
                                        );
                                    })
                                )}
                            </Stack>
                        </ScrollArea>
                    )}
                </Paper>

                <Paper withBorder radius="md" p="sm">
                    <Stack gap="sm">
                        <Textarea
                            label="Svar"
                            placeholder="Skriv en besked til brugeren..."
                            minRows={4}
                            autosize
                            value={message}
                            onChange={(e) => setMessage(e.currentTarget.value)}
                        />

                        <Group justify="flex-end">
                            <Button
                                leftSection={<IconSend size={16} />}
                                onClick={handleSend}
                                loading={sending}
                                disabled={!message.trim() || !bookingId}
                            >
                                Send WhatsApp
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            </Stack>
        </Drawer>
    );
}